import { useCallback, useRef, useState } from 'react';

export interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Tracks a single "traveling highlight" rectangle for a group of sibling
 * hoverable/focusable items, measured relative to a shared container.
 *
 * Renders as ONE persistent element that glides between positions (via the
 * caller's own `motion.span` + `animate`), rather than separate elements
 * mounting/unmounting per item. A shared `layoutId` across many
 * conditionally-mounted elements can flicker — mounting the new instance and
 * unmounting the old one race, causing a one-frame flash before the
 * animation catches up. Measuring and moving ONE never-unmounted element
 * sidesteps that class of bug entirely.
 */
export function useHoverHighlight<C extends HTMLElement = HTMLElement>() {
  const containerRef = useRef<C>(null);
  const [rect, setRect] = useState<HighlightRect | null>(null);

  /** Call on hover/focus of an item, passing that item's own element. */
  const measure = useCallback((el: HTMLElement | null) => {
    const container = containerRef.current;
    if (!el || !container) return;
    const c = container.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - c.top, left: r.left - c.left, width: r.width, height: r.height });
  }, []);

  /** Call when the pointer/focus leaves the whole group. */
  const clear = useCallback(() => setRect(null), []);

  return { containerRef, rect, measure, clear };
}
